import { useEffect } from "react";
import { Link } from "react-router-dom";
import { PRODUCT_MARKS } from "../brand/marks";
import { VideoBlock } from "../components/VideoBlock";
import { ALL_PRODUCTS } from "../nav/navigation";
import type { CtaLink, ProductPageContent } from "../content/types";

function Cta({ cta, variant }: { cta: CtaLink; variant: "primary" | "quiet" }) {
  const className = variant === "primary" ? "btn btn--primary" : "btn btn--quiet";
  if (cta.external) {
    return (
      <a className={className} href={cta.href}>
        {cta.label}
      </a>
    );
  }
  if (cta.href.startsWith("#")) {
    return (
      <a className={className} href={cta.href}>
        {cta.label}
      </a>
    );
  }
  return (
    <Link className={className} to={cta.href}>
      {cta.label}
    </Link>
  );
}

/**
 * Product page template. Sections render in a fixed order and only when the
 * content object supplies them, so an unfinished page is short rather than
 * padded with claims we cannot support.
 */
export function ProductPage({ content }: { content: ProductPageContent }) {
  const product = ALL_PRODUCTS.find((p) => p.slug === content.slug);
  const Mark = product ? PRODUCT_MARKS[product.mark] : undefined;

  useEffect(() => {
    document.title = product
      ? `${product.name} — ${product.descriptor} — Odingard`
      : "Odingard";
  }, [product]);

  return (
    <main className="page pdp" id="main">
      <section className="pdp__hero">
        {product && Mark && (
          <p className="pdp__lockup" style={{ color: product.accent }}>
            <Mark size={34} />
            <b>{product.name}</b>
            <span>{product.descriptor}</span>
          </p>
        )}
        <h1>{content.proofClaim}</h1>
        <p className="page__lede">{content.lede}</p>
        {content.availability && (
          <p className="pdp__availability">{content.availability}</p>
        )}
        <div className="pdp__ctas">
          {content.ctas.map((cta, i) => (
            <Cta key={cta.href} cta={cta} variant={i === 0 ? "primary" : "quiet"} />
          ))}
        </div>
      </section>

      {content.problem && (
        <section className="pdp__section" id="problem">
          <h2>{content.problem.heading}</h2>
          <p className="pdp__body">{content.problem.body}</p>
          <ol className="pdp__steps">
            {content.problem.points.map((point) => (
              <li key={point.title}>
                <b>{point.title}</b>
                <span>{point.body}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {content.video && (
        <section className="pdp__section" id="video">
          <h2>{content.video.heading}</h2>
          <p className="pdp__body">{content.video.body}</p>
          <VideoBlock video={content.video} />
        </section>
      )}

      {content.capabilities && (
        <section className="pdp__section" id="capabilities">
          <h2>{content.capabilities.heading}</h2>
          {content.capabilities.lede && (
            <p className="pdp__body">{content.capabilities.lede}</p>
          )}
          <div className="pdp__features">
            {content.capabilities.items.map((item) => (
              <article key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      {content.verticals && (
        <section className="pdp__section" id="verticals">
          <h2>{content.verticals.heading}</h2>
          <p className="pdp__body">{content.verticals.lede}</p>
          <div className="pdp__verticals">
            {content.verticals.items.map((v) => (
              <article key={v.name}>
                <p className="pdp__vertical-kicker">
                  <b>{v.name}</b>
                  <span>{v.status}</span>
                </p>
                <h3>{v.headline}</h3>
                <dl>
                  <dt>Today</dt>
                  <dd>{v.today}</dd>
                  <dt>With {product?.name ?? "it"}</dt>
                  <dd>{v.withProduct}</dd>
                  <dt>Never leaves</dt>
                  <dd>{v.neverLeaves}</dd>
                  <dt>Why it matters</dt>
                  <dd>{v.whyItMatters}</dd>
                </dl>
              </article>
            ))}
          </div>
          {content.verticals.note && (
            <p className="pdp__verticals-note">{content.verticals.note}</p>
          )}
        </section>
      )}

      {content.evidence && (
        <section className="pdp__section pdp__section--evidence" id="evidence">
          <h2>{content.evidence.heading}</h2>
          <p className="pdp__body">{content.evidence.lede}</p>
          <div className="pdp__stats">
            {content.evidence.stats.map((stat) => (
              <article key={stat.label}>
                <b>{stat.value}</b>
                <span className="pdp__stat-label">{stat.label}</span>
                {stat.note && <small>{stat.note}</small>}
              </article>
            ))}
          </div>
          {content.evidence.coverage && (
            <div className="pdp__coverage">
              {content.evidence.coverage.map((group) => (
                <article key={group.heading}>
                  <h3>{group.heading}</h3>
                  <ul>
                    {group.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  {group.note && <small>{group.note}</small>}
                </article>
              ))}
            </div>
          )}
          <p className="pdp__methodology">
            <b>Methodology.</b> {content.evidence.methodology}
          </p>
          {content.evidence.link && (
            <Cta cta={content.evidence.link} variant="quiet" />
          )}
        </section>
      )}

      {content.editions && (
        <section className="pdp__section" id="editions">
          <h2>{content.editions.heading}</h2>
          {content.editions.lede && (
            <p className="pdp__body">{content.editions.lede}</p>
          )}
          <div className="pdp__editions">
            {content.editions.items.map((edition) => (
              <article key={edition.name}>
                <h3>{edition.name}</h3>
                {edition.price && <p className="pdp__price">{edition.price}</p>}
                <p>{edition.blurb}</p>
                <ul>
                  {edition.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
                {edition.cta && <Cta cta={edition.cta} variant="quiet" />}
              </article>
            ))}
          </div>
          {content.editions.addOns && (
            <div className="pdp__addons" id="add-ons">
              <h3>{content.editions.addOns.heading}</h3>
              {content.editions.addOns.lede && (
                <p className="pdp__body">{content.editions.addOns.lede}</p>
              )}
              <dl>
                {content.editions.addOns.items.map((addOn) => (
                  <div key={addOn.name}>
                    <dt>
                      {addOn.name}
                      <span className="pdp__price">{addOn.price}</span>
                    </dt>
                    <dd>{addOn.note}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </section>
      )}

      {content.services && (
        <section className="pdp__section pdp__section--warden" id="services">
          <p className="page__eyebrow">
            {content.services.eyebrow ?? "Warden · By Odingard"}
          </p>
          <h2>{content.services.heading}</h2>
          <p className="pdp__body">{content.services.body}</p>
          <ul className="pdp__links">
            {content.services.links.map((link) => (
              <li key={link.href}>
                <Link to={link.href}>{link.label} →</Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {content.resources && (
        <section className="pdp__section" id="resources">
          <h2>{content.resources.heading}</h2>
          <div className="pdp__resources">
            {content.resources.items.map((item) =>
              item.external ? (
                <a key={item.href} href={item.href}>
                  <span className="pdp__kind">{item.kind}</span>
                  <b>{item.label}</b>
                </a>
              ) : (
                <Link key={item.href} to={item.href}>
                  <span className="pdp__kind">{item.kind}</span>
                  <b>{item.label}</b>
                </Link>
              ),
            )}
          </div>
        </section>
      )}

      <section className="pdp__close" id="close">
        <h2>{content.close.heading}</h2>
        <p className="pdp__body">{content.close.body}</p>
        <div className="pdp__ctas">
          {content.close.ctas.map((cta, i) => (
            <Cta key={cta.href} cta={cta} variant={i === 0 ? "primary" : "quiet"} />
          ))}
        </div>
      </section>
    </main>
  );
}
